import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import useTranslation from "components/customHooks/translations";
import { routeNames } from "routes";
import { useLoadingContext } from "components/contexts/LoadingContext";
import { IUser } from "interfaces";
import { getUsers } from "services/users";
import EnhancedTableHead, {
  HeadCell,
  Order,
} from "components/Table/EnhancedTableHead";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridToolbar,
} from "@mui/x-data-grid";
import ExportToolbar from "components/ExportToolbar";

interface Data {
  id: number;
  companyName: string;
  name: string;
  email: string;
  phone: string;
}

const headCells: HeadCell[] = [
  {
    id: "companyName",
    numeric: false,
    disablePadding: false,
    label: "Company Name",
    sorting: false,
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Contact Name",
    sorting: false,
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "Email",
    sorting: false,
  },
  {
    id: "phone",
    numeric: false,
    disablePadding: false,
    label: "Phone",
    sorting: false,
  },
];

const Customers = () => {
  const { pathname } = useLocation();
  const translation = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<IUser[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [pagesNumber, setPagesNumber] = useState(0);
  const [rows, setRows] = useState<Data[]>([]);
  const { loading, setLoading } = useLoadingContext();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<string>("id");
  const [selected, setSelected] = React.useState<readonly string[]>([]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick: GridEventListener<"rowClick"> = (params) => {
    navigate("/customers/" + params.row.id);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns: GridColDef[] = [
    { field: "companyName", headerName: "Company Name", flex: 1 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
  ];

  const customersToData = (customers: IUser[]) => {
    const data = customers.map((customer: IUser) => {
      return {
        id: customer.id ? customer.id : 0,
        companyName: customer.companyInfo ? customer.companyInfo.name : "-",
        name: customer.firstName + " " + customer.lastName,
        email: customer.email,
        phone: customer.phone ? customer.phone : "-",
      };
    });

    return data;
  };

  useEffect(() => {
    setLoading(true);

    getUsers(page + 1, rowsPerPage)
      .then((res) => {
        setCustomers(res.data["hydra:member"]);
        setRows(customersToData(res.data["hydra:member"]));
        setOrdersCount(res.data["hydra:totalItems"]);
        setPagesNumber(Math.ceil(res.data["hydra:totalItems"] / rowsPerPage));
        setLoading(false);
      })
      .catch((e) => console.log(e));
  }, [page, rowsPerPage]);

  return (
    <Box>
      <Stack direction="row">
        <Button
          variant="contained"
          onClick={() => navigate(routeNames.newCustomers)}
        >
          {translation.customers.createCustomerButton}
        </Button>
      </Stack>
      {rows.length > 0 ? (
        <>
          <Box sx={{ width: "100%", my: 2 }}>
            <DataGrid
              rows={rows}
              onRowClick={handleClick}
              sx={{
                // disable cell selection style
                ".MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                // pointer cursor on ALL rows
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                },
              }}
              columns={columns}
              slots={{ toolbar: ExportToolbar }}
            />
          </Box>
        </>
      ) : (
        !loading && (
          <Box sx={{ mt: 3 }}>
            <Typography>{translation.customers.noCustomers}</Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default Customers;
